import classNames from 'classnames';
import React from 'react';
import { AsyncState } from 'shared/utils/async-state';
import UDEntityList from 'ud-ui/components/entity-list';

import UDPagedListPager from './components/paged-list-pager';

export interface UDPagedListProps<T> {
  /** Элементы текущей страницы. Резать их компонент не будет — это работа хука. */
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Ключ строки. По умолчанию — позиция в списке. */
  getItemKey?: (item: T, index: number) => React.Key;

  /** Какую ветку рисовать; `isRefreshing` притушивает уже показанное. */
  state: AsyncState;

  page: number;
  /** Наибольший известный номер страницы. При `<= 1` пагинатор скрыт. */
  totalPages: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  /** Адрес страницы списка с другим номером — для `<a href>` в пагинаторе. */
  pageHref?: (page: number) => string;

  /** Значение поля поиска (черновик из `usePagedList`). */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Дополнительные фильтры страницы, рисуются в тулбаре. */
  extraFilters?: React.ReactNode;
  /** Кнопки действий над списком, рисуются в тулбаре справа. */
  actions?: React.ReactNode;

  /** Текст, когда список пуст сам по себе. */
  emptyText?: React.ReactNode;
  emptySubtitle?: React.ReactNode;
  /** Кнопка под текстом пустого состояния — обычно «Добавить …». */
  emptyAction?: React.ReactNode;
  /** Текст, когда пуст результат поиска. */
  notFoundText?: React.ReactNode;
  errorText?: React.ReactNode;

  className?: string;
  listClassName?: string;
  /**
   * Раскладка самих элементов. По умолчанию — колонка строк; страница с
   * карточками передаёт свою сетку. Значение **заменяет** дефолт, а не
   * дописывается к нему: `display` от двух классов сразу не складывается.
   */
  itemsClassName?: string;
  /** Высота одной заглушки скелетона: у строки и у карточки она разная. */
  skeletonRowHeight?: number;
}

/**
 * Страница-категория: тулбар, список, пагинатор.
 *
 * Это `UDEntityList` плюс две вещи, которые есть только у списка-страницы:
 * «На странице» в тулбаре и пагинатор под списком. Всё остальное — раскладка
 * строк, поиск, ветки загрузки/пустоты/ошибки — приходит из общего ядра, поэтому
 * список-страница и список внутри карточки выглядят одинаково по построению, а не
 * по договорённости.
 *
 * Компонент **чисто презентационный**: он не знает ни про URL, ни про RTK Query,
 * ни про роуты — всё приходит пропсами, ровно в том виде, в каком это отдаёт
 * `usePagedList`. Так `ud-ui` остаётся домен-независимым набором (правило
 * «`ud-ui` не импортирует фичи» из `.claude/rules/architecture.md`).
 *
 * Строку рисует сама страница через `renderItem` — компонент не принимает готовые
 * элементы `children`, как это делал `CategoryListPage`: там страница была обязана
 * отрендерить все накопленные элементы, а компонент резал уже готовые узлы.
 */
function UDPagedList<T>({ page, totalPages, onPageChange, pageHref, className, ...listProps }: UDPagedListProps<T>) {
  return (
    <UDEntityList
      {...listProps}
      className={classNames('px-6 pt-6', className)}
      footer={
        <UDPagedListPager
          className="mt-auto py-4"
          totalPages={totalPages}
          currentPage={page}
          onPageChange={onPageChange}
          pageHref={pageHref}
        />
      }
    />
  );
}

export default UDPagedList;
